import { EmailVerificationScreenProps } from '@hellodarwin/app-core/lib/providers/login-provider/types';
import Button from '@hellodarwin/core/lib/components/common/button';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { Language } from '@hellodarwin/core/lib/features/entities/translations_ns';
import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import useLocale, {
  getOtherLocale,
} from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import HdLogo from '@hellodarwin/icons/dist/icons/HdLogo';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useTheme } from 'styled-components';
import OnboardingCarrousel from '../../modules/onboarding-flow/components/common/OnboardingLayout/preview-container/components/carrousel';
import { getCarouselImages } from '../../modules/onboarding-flow/components/common/OnboardingLayout/preview-container/components/carrousel/content/images';
import {
  Actions,
  AppHeader,
  Content,
  EmailHighlight,
  Header,
  HeaderDivider,
  HeaderUtilities,
  Layout,
  LogoutButton,
  LocaleButton,
  Preview,
  ResendAction,
  Screen,
} from './styles';

const EmailVerificationScreen = ({
  email,
  accessToken,
  handleLogout,
  refreshEmailVerification,
  resendEmailVerification,
}: EmailVerificationScreenProps) => {
  const theme = useTheme();
  const { t } = useTranslations();
  const { selectedLocale, changeLanguage } = useLocale();
  const otherLocale = getOtherLocale(selectedLocale) ?? 'en';
  const carouselImages = getCarouselImages(selectedLocale);
  const normalizedEmail = email?.trim();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [hasResentVerificationEmail, setHasResentVerificationEmail] =
    useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshEmailVerification();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleResend = async () => {
    if (!resendEmailVerification) return;

    setIsResending(true);
    try {
      await resendEmailVerification(accessToken);
      setHasResentVerificationEmail(true);
      antdMessage.success(t('common|emailVerification.resendSuccess'));
    } catch {
      antdMessage.error(t('common|emailVerification.resendError'));
    } finally {
      setIsResending(false);
    }
  };

  const handleLocaleChange = () => {
    changeLanguage(otherLocale as Language);
  };

  return (
    <Layout>
      <AppHeader>
        <HdLogo backgroundType="light" color="b/w" width={143} height={18} />

        <HeaderUtilities>
          <LocaleButton
            defaultStyle={theme.colors.transparent}
            key={otherLocale}
            onClick={handleLocaleChange}
            size="square"
          >
            {otherLocale}
          </LocaleButton>
          <HeaderDivider />
          <LogoutButton onClick={handleLogout} size="auto" variant="text">
            {t('common|emailVerification.logout')}
          </LogoutButton>
        </HeaderUtilities>
      </AppHeader>

      <Screen>
        <Content>
          <Header>
            <Typography.Title level={4} elementTheme="h2" textAlign="left">
              {t('common|emailVerification.title')}
            </Typography.Title>
            <Typography.Paragraph elementTheme="body1">
              {normalizedEmail ? (
                <Trans
                  i18nKey="common|emailVerification.description"
                  values={{ email: normalizedEmail }}
                  components={{ email: <EmailHighlight /> }}
                />
              ) : (
                t('common|emailVerification.descriptionWithoutEmail')
              )}
            </Typography.Paragraph>
          </Header>
          <Actions>
            <Button
              defaultStyle={theme.colors.grey_1}
              loading={isRefreshing}
              onClick={handleRefresh}
              shape="rounded-corner"
              size="medium"
              tablet={{ fullWidth: true }}
            >
              {t('common|emailVerification.refresh')}
            </Button>
            {!!resendEmailVerification && (
              <ResendAction>
                <Typography
                  elementTheme="caption"
                  color={theme.colors.grey_2}
                  fitContent
                >
                  {t('common|emailVerification.resendPrompt')}
                </Typography>
                <Button
                  disabled={hasResentVerificationEmail}
                  fitContent
                  loading={isResending}
                  onClick={handleResend}
                  variant="text"
                >
                  {t('common|emailVerification.resend')}
                </Button>
              </ResendAction>
            )}
          </Actions>
        </Content>
        <Preview>
          <OnboardingCarrousel images={carouselImages} />
        </Preview>
      </Screen>
    </Layout>
  );
};

export default EmailVerificationScreen;
